import isBrowser from './isBrowser';
import { captureExceptionWithState } from './sentry';

type BaseStorage = {
  getItem: (name: string) => string | null;
  setItem: (name: string, value: string) => void | Promise<void>;
  removeItem: (name: string) => void | Promise<void>;
};

type StateStorage = BaseStorage & {
  _config: Config;
  getScopedKey: (key: string, scopedKey?: string | null | undefined) => string;
  isBrowser: () => boolean;
};

type Config = {
  delimiter: string;
  prefix: boolean;
  postfix: boolean;
  scopedKey: string;
};
/**
 * A simple storage abstraction around local storage
 * A State storage must have the following interface
  @see https://github.com/pmndrs/zustand/wiki/Persisting-the-store's-data#Options
  @todo https://app.clickup.com/t/8677g9n53
*/
const getStorage: () => StateStorage = () => ({
  _config: { delimiter: '_', prefix: false, postfix: false, scopedKey: '' },
  isBrowser: () => isBrowser,
  removeItem(key: string) {
    const withinBrowser = this.isBrowser();
    if (!withinBrowser) {
      return;
    }
    localStorage.removeItem(key);
  },
  getItem(key: string) {
    const withinBrowser = this.isBrowser();
    if (!withinBrowser) {
      return null;
    }
    try {
      const item = localStorage.getItem(key);
      return item;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('could not parse localStorage');
      return null;
    }
  },
  setItem(key: string, value: string) {
    const withinBrowser = this.isBrowser();
    if (!withinBrowser) {
      return;
    }
    try {
      localStorage.setItem(key, value);
    } catch (err) {
      if (err instanceof DOMException && err.name === 'QuotaExceededError') {
        captureExceptionWithState(err, {
          type: 'LocalStorage',
          value: {
            key,
            payloadBytes: value.length,
          },
        });
      }
    }
  },
  /**
   * Derive a namespaced key for storage get/set ability
   * @param key
   * @param scopedKey
   */
  getScopedKey(key: string, scopedKey?: string | null | undefined) {
    const _scopedKey = this._config.scopedKey || scopedKey || '';
    return key && scopedKey ? `${key}_${_scopedKey}` : key;
  },
});

export default getStorage();
